import {AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {SignaturePadComponent} from '@shared/controls/signature-pad/signature-pad/signature-pad.component';
import {BaseModalService, MODAL_DATA_TOKEN} from '@paperlessio/sdk/modal/base-modal';
import {UploadService} from '@shared/services/upload.service';
import {base64toBlob} from '@paperlessio/sdk/util/helpers';
import {PointGroup} from 'signature_pad';
import {TypedSignaturePadComponent} from '@shared/controls/signature-pad/typed-signature-pad/typed-signature-pad.component';
import {Subscription} from 'rxjs';
import {Blob, Participant, SignatureModalTabs, User} from '@paperlessio/sdk/api/models'
import {Fonts} from '@shared/controls/signature-pad/font-list';
import {filter} from 'rxjs/operators';
import {AuthenticationStore} from '@paperlessio/sdk/api/stores';
import {TabsComponent} from '@shared/controls/tabs/tabs/tabs.component';
import {SessionStore} from '../../submission/src/app/session/session.store';
import {SubmissionSignatureStorage} from './submission-signature-storage';
import {ToastService} from '@paperlessio/sdk/api/util';
import * as uuid from 'uuid';

@Component({
  selector: 'app-signature-input-modal',
  templateUrl: './signature-input-modal.component.html',
  styleUrls: ['./signature-input-modal.component.scss']
})
export class SignatureInputModalComponent implements OnInit, OnDestroy, AfterViewInit {
  baseModalService = inject(BaseModalService, {optional: true});
  sessionStore = inject(SessionStore, {optional: true});
  authStore = inject(AuthenticationStore, {optional: true});
  toast = inject(ToastService);

  @ViewChild(SignaturePadComponent) signaturePadComponent: SignaturePadComponent;
  @ViewChild(TypedSignaturePadComponent) typedSignaturePadComponent: TypedSignaturePadComponent;
  @ViewChild(TabsComponent) tabsComponent: TabsComponent;

  SignatureModalTabs = SignatureModalTabs;
  disabledSignatureModalTabs = [];

  user: User;
  participant: Participant;
  selectedFile: File;
  localFile: string;
  selectedFont = Fonts[0].id;
  initialTabName: SignatureModalTabs;

  private uploadService = inject(UploadService, {});
  private modalData = inject(MODAL_DATA_TOKEN, {}) as { signature_type?: SignatureModalTabs, local_image?: string, signature_text?: string };
  private data: PointGroup[];
  private subs = new Subscription();

  ngOnInit() {
    if (this.modalData?.signature_type === SignatureModalTabs.uploaded) {
      this.localFile = this.modalData?.local_image;
    }

    this.subs.add(this.sessionStore?.submissionMetaInformation
      .pipe(filter(v => !!v))
      .subscribe(smi => {
        this.participant = smi.participant;
        if (smi.submittable?.settings?.disabledSignatureModalTabs?.length) {
          this.disabledSignatureModalTabs.push(...smi.submittable.settings.disabledSignatureModalTabs);
        }

        const localSignature = new SubmissionSignatureStorage().getSignatureInfo(this.participant.email);

        if (this.modalData?.signature_type) {
          // If the modal gets a signature, use that.
          this.initialTabName = this.modalData.signature_type;
        } else if (localSignature) {
          // If the user signed in a document and has actively decided against using the eventually stored one, 'use' the 'newer' signature
          this.initialTabName = SignatureModalTabs.drawn;
        } else if (this.participant.stored_signature_image?.default_url && this.participant?.allow_stored_signature_image_use) {
          // Stored signature is present & allowed. Use that.
          this.initialTabName = SignatureModalTabs.stored;
        } else {
          // Default
          this.initialTabName = [
            SignatureModalTabs.typed,
            SignatureModalTabs.drawn,
            SignatureModalTabs.uploaded
          ].find(type => !this.disabledSignatureModalTabs.includes(type));
        }
      }));

    this.subs.add(this.authStore?.currentUser.subscribe(user => this.user = user));
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.initialTabName === SignatureModalTabs.typed && this.modalData?.signature_text) {
        this.typedSignaturePadComponent.signatureText = this.modalData?.signature_text;
        this.typedSignaturePadComponent.drawSignature();
      }

      // this.typedSignaturePadComponent?.focus();

      this.tabsComponent.focusActiveHeader();
    }, 50);
  }

  tabChange() {
    this.localFile = null;

    // Only run this if we're in submission
    if (this.participant) {
      // if the SubmissionSignatureStorage has eligible data, place it in the pad
      if (this.signaturePadComponent && this.signaturePadComponent.toData()?.length <= 0) {
        const signatureInfo = new SubmissionSignatureStorage().getSignatureInfo(this.participant.email);
        if (signatureInfo != null && signatureInfo.signature.length > 0) {
          this.signaturePadComponent.fromData(signatureInfo.signature, signatureInfo.signaturePadSize);
        }
      }
    }
  }

  public async sign() {
    let file: File;
    let localImage: any;
    let signedId: string;
    let valid = false;

    switch (this.tabsComponent.activeTabName) {
      default:
      case SignatureModalTabs.drawn: {
        const dataUrl = this.signaturePadComponent.toDataURL('image/svg+xml');
        const b64Data = dataUrl.replace('data:image/svg+xml;base64,', '');
        const blob = base64toBlob(b64Data, 'image/svg+xml');

        valid = !this.signaturePadComponent.isEmpty();

        localImage = dataUrl;
        file = new File([blob], 'signature.svg', {type: 'image/svg+xml'});

        if (this.participant) {
          const pointData = this.signaturePadComponent.toData();

          const signatureWidth = this.signaturePadComponent?.canvasRef?.nativeElement?.offsetWidth ?? 0;
          const signatureHeight = this.signaturePadComponent?.canvasRef?.nativeElement?.offsetHeight ?? 0;
          new SubmissionSignatureStorage().addSignature(this.participant.email, pointData, {x: signatureWidth, y: signatureHeight});
        }

        break;
      }
      case SignatureModalTabs.typed: {
        const dataUrl = this.typedSignaturePadComponent.toDataURL('image/png');
        const b64Data = dataUrl.replace('data:image/png;base64,', '');
        const blob = base64toBlob(b64Data, 'image/png');

        valid = this.typedSignaturePadComponent.valid();

        localImage = dataUrl;
        file = new File([blob], 'signature.png', {type: 'image/png'});

        break;
      }
      case SignatureModalTabs.uploaded: {
        file = this.selectedFile as File;
        localImage = this.localFile;

        valid = file != null && localImage != null;

        break;
      }
      case SignatureModalTabs.stored: {
        this.baseModalService?.close({
          signature_image_signed_id: this.participant.stored_signature_image.signed_id,
          signature_image: this.participant.stored_signature_image.default_url,
          signature_type: this.tabsComponent.activeTabName
        });

        return;
      }
    }

    // If there was a file when we opened the modal & no new image has been selected in the upload tab, just close the modal.
    if (!file
      && this.modalData?.signature_type === SignatureModalTabs.uploaded
      && this.tabsComponent.activeTabName === SignatureModalTabs.uploaded
      && this.modalData?.local_image != null) {
      this.baseModalService?.close();
      return;
    }

    if (!valid || (!file && !signedId)) {
      this.toast.error('signature_input.modal.errors.invalid_signature');
      return;
    }

    const blob = Blob.forUpload(file, uuid.v4());

    this.uploadService
      .upload(blob)
      .subscribe(uploadedBlob => {
        this.baseModalService?.close({
          signature_image_signed_id: uploadedBlob.signed_id,
          signature_image: localImage,
          signature_type: this.tabsComponent.activeTabName,
          signature_text: this.tabsComponent.activeTabName === SignatureModalTabs.typed ? this.typedSignaturePadComponent?.signatureText : null
        });
      });

    // TODO
    // if (this.sessionStore?.session?.value && this.block_id) {
    //   this.sessionStore.recordEvent({block_id: this.block_id, action: 'sign'}).subscribe();
    // }
  }

  public onFile(f: File) {
    this.selectedFile = f;

    const reader = new FileReader();
    reader.readAsDataURL(f);
    reader.onload = () => {
      this.localFile = reader.result as string;
    };
  }

  public reset(): void {
    this.data = null;
    this.selectedFile = null;
    this.localFile = null;
    this.typedSignaturePadComponent?.clear();
    this.signaturePadComponent?.clear();
  }

  resetSignatureStore() {
    if (this.participant) {
      new SubmissionSignatureStorage().removeSignature(this.participant.email);
    }
  }

  async selectFont(fontId: string) {
    this.selectedFont = fontId;
    await this.typedSignaturePadComponent.selectFont(fontId);
  }
}
